<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useTasksStore } from 'dashboard/stores/tasks';

import TasksListLayout from 'dashboard/components-next/CRM/TasksListLayout.vue';
import TasksCard from 'dashboard/components-next/CRM/TasksCard/TasksCard.vue';
import TasksEmptyState from 'dashboard/components-next/CRM/EmptyState/TasksEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CreateTaskDialog from 'dashboard/components-next/CRM/TasksForm/CreateTaskDialog.vue';

const DEBOUNCE_DELAY = 300;

const tasksStore = useTasksStore();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const tasks = computed(() => tasksStore.getTasksList);
const meta = computed(() => tasksStore.getMeta);
const uiFlags = computed(() => tasksStore.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const sortQuery = computed(() => route.query?.sort || 'due_date_asc');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const isFetchingList = computed(() => uiFlags.value.fetchingList);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);

const hasTasks = computed(() => tasks.value.length > 0);
const isTaskIndexView = computed(
  () => route.name === 'crm_tasks_index' && pageNumber.value === 1
);

const showEmptyStateLayout = computed(() => {
  return !searchQuery.value && !hasTasks.value && isTaskIndexView.value;
});

const showEmptyText = computed(() => {
  return (searchQuery.value || !isTaskIndexView.value) && !hasTasks.value;
});

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchTasks = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortQuery.value;

  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  await tasksStore.get({
    page: currentPage,
    search: currentSearch,
    sort: currentSort,
  });
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchTasks(1, query, sortQuery.value);
}, DEBOUNCE_DELAY);

const onSortChange = sort => {
  fetchTasks(pageNumber.value, searchQuery.value, sort);
};

const onPageChange = page => {
  fetchTasks(page, searchValue.value, sortQuery.value);
};

const handleShowTask = taskId => {
  router.push(`/app/accounts/${route.params.accountId}/crm/tasks/${taskId}`);
};

const handleCompleteTask = async taskId => {
  try {
    await tasksStore.complete(taskId);
    useAlert(t('TASKS.COMPLETE_SUCCESS'));
  } catch (error) {
    useAlert(t('TASKS.COMPLETE_ERROR'));
  }
};

const createTaskDialogRef = ref(null);

const handleAddTask = () => {
  createTaskDialogRef.value?.open();
};

const handleCreateTask = async taskData => {
  try {
    await tasksStore.create(taskData);
    createTaskDialogRef.value?.onSuccess();
    useAlert(t('TASKS.CREATE_SUCCESS'));
    fetchTasks();
  } catch (error) {
    useAlert(t('TASKS.CREATE_ERROR'));
  }
};

const emptyStateMessage = computed(() => {
  if (!searchQuery.value) {
    return t('TASKS.EMPTY_STATE.LIST_EMPTY_STATE_TITLE');
  }
  return t('TASKS.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE');
});

onMounted(() => {
  searchValue.value = searchQuery.value;
  fetchTasks();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <TasksListLayout
      :search-value="searchValue"
      :header-title="$t('TASKS.TITLE')"
      :current-page="pageNumber"
      :total-items="Number(meta.totalCount || 0)"
      :active-sort="sortQuery"
      :is-fetching-list="isFetchingList"
      :show-pagination-footer="!!tasks.length"
      @update:current-page="onPageChange"
      @update:sort="onSortChange"
      @search="onSearch"
      @add="handleAddTask"
    >
      <div
        v-if="isFetchingList"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <TasksEmptyState
          v-if="showEmptyStateLayout"
          class="pt-14"
          :title="$t('TASKS.EMPTY_STATE.TITLE')"
          :subtitle="$t('TASKS.EMPTY_STATE.SUBTITLE')"
          :button-label="$t('TASKS.EMPTY_STATE.BUTTON_LABEL')"
          @create="handleAddTask"
        />

        <div
          v-else-if="showEmptyText"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ emptyStateMessage }}
          </span>
        </div>

        <div v-else class="flex flex-col gap-4 px-6 pt-4 pb-6">
          <TasksCard
            v-for="task in tasks"
            :key="task.id"
            :id="task.id"
            :title="task.title"
            :task-type="task.taskType"
            :status="task.status"
            :priority="task.priority"
            :due-date="task.dueDate"
            :user-name="task.user?.name"
            :taskable-type="task.taskableType"
            :taskable-name="task.taskable?.name"
            :updated-at="task.updatedAt"
            @show-task="handleShowTask"
            @complete="handleCompleteTask"
          />
        </div>
      </template>
    </TasksListLayout>
    
    <CreateTaskDialog
      ref="createTaskDialogRef"
      @create="handleCreateTask"
    />
  </div>
</template>
